import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL as API } from '../../apiConfig';
import { getInternalFleetCopy } from '../../pages/internal/internalFleetI18n';
import VehicleMaintenance from './VehicleMaintenance';
import { CarFront, History, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

const REQUIRED_VEHICLE_PROFILE_FIELDS = [
  'front_photo_url',
  'seat_photo_url',
  'chassis_number',
  'max_passengers',
];

const compressVehiclePhoto = (file, maxSize = 1280, quality = 0.78) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const image = new Image();
    image.onerror = reject;
    image.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    image.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const getVehicleCompletionStatus = (vehicle) => {
  const missingFields = REQUIRED_VEHICLE_PROFILE_FIELDS.filter((field) => !vehicle?.[field]);
  return {
    isComplete: missingFields.length === 0,
    missingFields,
  };
};

const FleetVehicles = ({ canEdit = true, language = 'zh' }) => {
  const copy = getInternalFleetCopy(language);
  const t = copy.vehicles;
  const common = copy.common;
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [historyVehicle, setHistoryVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [hideInactive, setHideInactive] = useState(true); // 默认隐藏已废弃/暂停

  const initialForm = {
    original_owner: '',
    original_plate: '',
    plate_number: '',
    chassis_number: '',
    max_passengers: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_color: '',
    vehicle_type: 'alphard',
    vehicle_type_other: '',
    front_photo_url: '',
    seat_photo_url: '',
    etc_card_number: '',
    has_dashcam: false,
    has_winter_tires: false,
    has_gps: false,
    status: 'active',
    insurance_type: 'basic',
    insurance_number: '',
    insurance_expiry: '',
    inspection_expiry: '',
    quarterly_inspection_start_date: '',
    quarterly_inspection_records: [],
    documents_link: '',
    assigned_driver_id: '',
    notes: '',
  };

  const [form, setForm] = useState(initialForm);

  const vehicleTypes = [
    { value: 'alphard', label: 'Alphard' },
    { value: 'hiace', label: 'Hiace' },
    { value: 'other', label: t.other },
  ];

  const statusOptions = [
    { value: 'active', label: t.activeStatus, color: 'bg-emerald-500' },
    { value: 'maintenance', label: t.maintenanceStatus, color: 'bg-amber-500' },
    { value: 'suspended', label: t.suspendedStatus, color: 'bg-blue-500' },
    { value: 'retired', label: t.retiredStatus, color: 'bg-slate-500' },
  ];

  const insuranceTypes = [
    { value: 'basic', label: t.basicInsurance },
    { value: 'full', label: t.fullInsurance },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const buildVehiclePayload = () => ({
    ...form,
    max_passengers: form.max_passengers === '' ? null : Number(form.max_passengers),
    vehicle_year: form.vehicle_year === '' ? null : Number(form.vehicle_year),
  });

  const fetchData = async () => {
    try {
      const [vehiclesRes, driversRes] = await Promise.all([
        axios.get(`${API}/api/internal/vehicles`),
        axios.get(`${API}/api/internal/drivers`),
      ]);
      setVehicles(vehiclesRes.data || []);
      setDrivers(driversRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildVehiclePayload();
      if (editingVehicle) {
        await axios.put(`${API}/api/internal/vehicles/${editingVehicle.id}`, payload);
      } else {
        await axios.post(`${API}/api/internal/vehicles`, payload);
      }
      fetchData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save vehicle:', error);
      alert('保存失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('确定要删除这辆车吗？关联的财务记录也会被删除。')) {
      try {
        await axios.delete(`${API}/api/internal/vehicles/${id}`);
        fetchData();
      } catch (error) {
        console.error('Failed to delete vehicle:', error);
        alert('删除失败');
      }
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingVehicle(null);
  };

  const openEditModal = (vehicle) => {
    setSelectedVehicle(null);
    setEditingVehicle(vehicle);
    setForm({
      original_owner: vehicle.original_owner || '',
      original_plate: vehicle.original_plate || '',
      plate_number: vehicle.plate_number || '',
      chassis_number: vehicle.chassis_number || '',
      max_passengers: vehicle.max_passengers || '',
      vehicle_make: vehicle.vehicle_make || '',
      vehicle_model: vehicle.vehicle_model || '',
      vehicle_year: vehicle.vehicle_year || '',
      vehicle_color: vehicle.vehicle_color || '',
      vehicle_type: vehicle.vehicle_type || 'alphard',
      vehicle_type_other: vehicle.vehicle_type_other || '',
      front_photo_url: vehicle.front_photo_url || '',
      seat_photo_url: vehicle.seat_photo_url || '',
      etc_card_number: vehicle.etc_card_number || '',
      has_dashcam: vehicle.has_dashcam || false,
      has_winter_tires: vehicle.has_winter_tires || false,
      has_gps: vehicle.has_gps || false,
      status: vehicle.status || 'active',
      insurance_type: vehicle.insurance_type || 'basic',
      insurance_number: vehicle.insurance_number || '',
      insurance_expiry: vehicle.insurance_expiry || '',
      inspection_expiry: vehicle.inspection_expiry || '',
      quarterly_inspection_start_date: vehicle.quarterly_inspection_start_date || '',
      quarterly_inspection_records: vehicle.quarterly_inspection_records || [],
      documents_link: vehicle.documents_link || '',
      assigned_driver_id: vehicle.assigned_driver_id || '',
      notes: vehicle.notes || '',
    });
    setShowModal(true);
  };

  const isExpiringWithinDays = (dateStr, days = 10) => {
    if (!dateStr) return false;
    const expiry = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diffDays <= days && diffDays > 0;
  };

  const isExpiringSoon = (dateStr) => {
    return isExpiringWithinDays(dateStr, 10);
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const addMonths = (dateStr, months) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1 + months, 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    date.setDate(Math.min(day, lastDay));
    return formatDate(date);
  };

  const normalizeQuarterlyRecords = (records) => {
    const actualByExpected = {};
    (Array.isArray(records) ? records : []).forEach((record) => {
      if (record?.expected_date) {
        actualByExpected[record.expected_date] = record.actual_date || '';
      }
    });
    return actualByExpected;
  };

  const buildQuarterlyInspectionSchedule = (vehicleOrForm) => {
    const startDate = vehicleOrForm.quarterly_inspection_start_date;
    const actualByExpected = normalizeQuarterlyRecords(vehicleOrForm.quarterly_inspection_records);
    const expectedDates = new Set(Object.keys(actualByExpected));
    const today = formatDate(new Date());

    if (startDate) {
      let expectedDate = startDate;
      let futureRows = 0;
      let guard = 0;
      while (guard < 200) {
        expectedDates.add(expectedDate);
        if (expectedDate >= today) {
          futureRows += 1;
          if (futureRows >= 4) break;
        }
        expectedDate = addMonths(expectedDate, 3);
        guard += 1;
      }
    }

    return Array.from(expectedDates).sort().map((expectedDate) => ({
      expected_date: expectedDate,
      actual_date: actualByExpected[expectedDate] || '',
    }));
  };

  const getNextOpenQuarterlyInspection = (vehicle) => {
    const schedule = vehicle.quarterly_inspection_schedule || buildQuarterlyInspectionSchedule(vehicle);
    return schedule.find((row) => !row.actual_date);
  };

  const updateQuarterlyActualDate = (expectedDate, actualDate) => {
    const records = Array.isArray(form.quarterly_inspection_records)
      ? [...form.quarterly_inspection_records]
      : [];
    const existingIndex = records.findIndex((record) => record.expected_date === expectedDate);
    const nextRecord = { expected_date: expectedDate, actual_date: actualDate };

    if (existingIndex >= 0) {
      records[existingIndex] = nextRecord;
    } else {
      records.push(nextRecord);
    }

    setForm({
      ...form,
      quarterly_inspection_records: records
        .filter((record) => record.expected_date)
        .sort((a, b) => a.expected_date.localeCompare(b.expected_date)),
    });
  };

  const handlePhotoUpload = async (field, file) => {
    if (!file) return;
    try {
      const compressedPhoto = await compressVehiclePhoto(file);
      setForm((current) => ({
        ...current,
        [field]: compressedPhoto || '',
      }));
    } catch (error) {
      console.error('Failed to compress vehicle photo:', error);
      alert('照片处理失败，请换一张照片再试');
    }
  };

  const getVehicleTypeLabel = (vehicle) => (
    vehicle.vehicle_type === 'other'
      ? vehicle.vehicle_type_other
      : vehicleTypes.find(t => t.value === vehicle.vehicle_type)?.label || vehicle.vehicle_type
  );

  const getVehicleExpirySummary = (vehicle) => {
    const nextQuarterly = getNextOpenQuarterlyInspection(vehicle);
    if (isExpired(vehicle.insurance_expiry)) return `保险${common.expired}`;
    if (isExpired(vehicle.inspection_expiry)) return `车检${common.expired}`;
    if (isExpired(nextQuarterly?.expected_date)) return `点检${common.incomplete}`;
    if (isExpiringSoon(vehicle.insurance_expiry)) return '保险即将到期';
    if (isExpiringSoon(vehicle.inspection_expiry)) return '车检即将到期';
    if (isExpiringWithinDays(nextQuarterly?.expected_date, 15)) return '点检即将到期';
    return '正常';
  };

  const renderCompletionBadge = (vehicle) => {
    const completion = getVehicleCompletionStatus(vehicle);
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
        completion.isComplete
          ? 'bg-emerald-500/20 text-emerald-300'
          : 'bg-red-500/20 text-red-300'
      }`}>
        <span aria-hidden="true">{completion.isComplete ? '✓' : '○'}</span>
        {completion.isComplete ? '资料完整' : '资料未完整'}
      </span>
    );
  };

  const renderPhotoPanel = (label, src, alt) => (
    <div className="space-y-2">
      <p className="text-slate-400 text-sm">{label}</p>
      <div className="aspect-[4/3] rounded-lg border border-slate-700 bg-slate-900/50 overflow-hidden flex items-center justify-center">
        {src ? (
          <img src={src} alt={alt} className="h-full w-full object-cover" />
        ) : (
          <span className="text-slate-500 text-sm">未上传</span>
        )}
      </div>
    </div>
  );

  const filteredVehicles = vehicles.filter(v => {
    const matchesSearch = v.plate_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          v.original_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          v.original_owner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          v.chassis_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          v.vehicle_make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          v.vehicle_model?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || v.status === filterStatus;
    // 一键隐藏已废弃/暂停
    const matchesHideInactive = !hideInactive || (v.status !== 'retired' && v.status !== 'suspended');
    return matchesSearch && matchesStatus && matchesHideInactive;
  });
  const quarterlyInspectionSchedule = buildQuarterlyInspectionSchedule(form);
  const todayStr = formatDate(new Date());
  const quarterlyHistoryRows = quarterlyInspectionSchedule.filter(row => row.expected_date < todayStr);
  const quarterlyFutureRows = quarterlyInspectionSchedule.filter(row => row.expected_date >= todayStr);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="internal-page-skin fleet-premium space-y-6 min-w-0">
      {/* Header */}
      <div className="fleet-vehicle-toolbar gap-4 justify-between">
        <div className="flex items-center gap-4 whitespace-nowrap">
          <input
            type="text"
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{common.allStatuses}</option>
            {statusOptions.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {/* 一键隐藏已废弃/暂停开关 */}
          <label className="flex items-center gap-2 bg-slate-700 px-4 py-2 rounded-lg cursor-pointer hover:bg-slate-600 transition-colors">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              className="w-4 h-4 text-blue-500 rounded focus:ring-blue-500"
            />
            <span className="text-white text-sm whitespace-nowrap">{t.hideInactive}</span>
          </label>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="add-vehicle-btn"
          disabled={!canEdit}
          title={!canEdit ? common.noEditPermission : ''}
        >
          <Plus size={16} aria-hidden="true" />{t.addVehicle}
        </button>
      </div>

      {/* Stats */}
      <VehicleMaintenance vehicles={vehicles} canEdit={canEdit} language={language} onSaved={fetchData} onOpenHistory={() => setHistoryVehicle({ id: '', plate_number: 'すべての車両' })} />
      <div className="fleet-vehicle-stats gap-4">
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-slate-400 text-sm">{t.total}</p>
          <p className="text-white text-2xl font-bold">{vehicles.length}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-slate-400 text-sm">{t.active}</p>
          <p className="text-emerald-400 text-2xl font-bold">{vehicles.filter(v => v.status === 'active').length}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-slate-400 text-sm">{t.maintenance}</p>
          <p className="text-amber-400 text-2xl font-bold">{vehicles.filter(v => v.status === 'maintenance').length}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-slate-400 text-sm">{t.expiring}</p>
          <p className="text-red-400 text-2xl font-bold">
            {vehicles.filter(v => {
              const nextQuarterly = getNextOpenQuarterlyInspection(v);
              return isExpiringSoon(v.insurance_expiry) ||
                isExpiringSoon(v.inspection_expiry) ||
                isExpiringWithinDays(nextQuarterly?.expected_date, 15);
            }).length}
          </p>
        </div>
      </div>

      {/* Vehicle List */}
      <div className="fleet-vehicle-ledger overflow-hidden">
        <div className="fleet-single-line-scroll" tabIndex={0} aria-label="車両一覧">
          <table className="fleet-single-line-table">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">照片</th>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">{t.plate}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">{t.vehicleType}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">定员</th>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">{t.assignedDriver}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">资料状态</th>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">到期提醒</th>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4 whitespace-nowrap">{common.status}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-6 py-4">点検・オイル交換履歴</th>
                <th className="text-center text-slate-400 text-sm font-medium px-6 py-4">{common.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredVehicles.map((vehicle) => (
                <tr
                  key={vehicle.id}
                  className="hover:bg-slate-700/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedVehicle(vehicle)}
                >
                  <td className="px-6 py-4">
                    {vehicle.front_photo_url ? (
                      <img
                        src={vehicle.front_photo_url}
                        alt={`${vehicle.plate_number || '车辆'}正面照片`}
                        className="h-12 w-16 rounded object-cover border border-slate-600"
                      />
                    ) : (
                      <div title="写真未登録" aria-label="写真未登録" className="fleet-photo-placeholder h-12 w-16 rounded flex items-center justify-center">
                        <CarFront size={24} strokeWidth={1.25} aria-hidden="true" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="fleet-vehicle-inline">
                      <span className="text-white font-medium">{vehicle.plate_number}</span>
                      <span className="text-xs text-cyan-300">{vehicle.tire_season === 'winter' ? '冬タイヤ' : vehicle.tire_season === 'summer' ? '夏タイヤ' : 'タイヤ未記録'}</span>
                      {vehicle.original_plate && (
                        <span className="text-slate-500 text-sm">{t.original}: {vehicle.original_plate}</span>
                      )}
                      {(vehicle.vehicle_make || vehicle.vehicle_model || vehicle.vehicle_year) && (
                        <span className="text-slate-500 text-sm">
                          {[vehicle.vehicle_make, vehicle.vehicle_model, vehicle.vehicle_year].filter(Boolean).join(' / ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-slate-300">
                      {getVehicleTypeLabel(vehicle)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-slate-300 whitespace-nowrap">
                      {vehicle.max_passengers ? `${vehicle.max_passengers}名` : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-slate-300">{vehicle.assigned_driver_name || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    {renderCompletionBadge(vehicle)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm ${
                      getVehicleExpirySummary(vehicle) === '正常' ? 'text-slate-300' : 'text-amber-300 font-medium'
                    }`}>
                      {getVehicleExpirySummary(vehicle)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`internal-status-badge ${
                      vehicle.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                      vehicle.status === 'maintenance' ? 'bg-amber-500/20 text-amber-400' :
                      vehicle.status === 'suspended' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {statusOptions.find(s => s.value === vehicle.status)?.label}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button type="button" aria-label={`${vehicle.plate_number} 点検・オイル交換履歴`} onClick={e => { e.stopPropagation(); setHistoryVehicle(vehicle); }} className="inline-flex items-center gap-2 text-cyan-300">
                      <History size={16} />履歴を見る
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {vehicle.documents_link && (
                      <a
                        href={vehicle.documents_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 mr-3"
                        title="查看文档"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📄
                      </a>
                    )}
                    {canEdit && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedVehicle(null); openEditModal(vehicle); }}
                          className="text-blue-400 hover:text-blue-300 mr-3"
                        >
                          {common.edit}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(vehicle.id); }}
                          className="text-red-400 hover:text-red-300"
                        >
                          {common.delete}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredVehicles.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            {t.empty}
          </div>
        )}
      </div>

      <Dialog open={Boolean(historyVehicle)} onOpenChange={open => { if (!open) setHistoryVehicle(null); }}>
        <DialogContent className="internal-page-skin fleet-premium fleet-history-dialog max-w-5xl max-h-[85dvh] min-w-0 overflow-y-auto rounded-lg bg-[#0c0d0e] p-5" aria-describedby={undefined}>
          <DialogTitle className="overflow-x-auto whitespace-nowrap mr-6">点検・オイル交換履歴 · {historyVehicle?.plate_number}</DialogTitle>
          {historyVehicle && <VehicleMaintenance key={historyVehicle.id || 'all'} vehicles={vehicles} initialVehicleId={historyVehicle.id} canEdit={canEdit} onSaved={fetchData} />}
        </DialogContent>
      </Dialog>

      {selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
          <div className="w-full max-w-4xl h-full bg-slate-800 border-l border-slate-700 overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 bg-slate-800/95 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-white text-xl font-bold">车辆详情</h3>
                <p className="text-slate-400 text-sm">{selectedVehicle.plate_number || '-'}</p>
              </div>
              <div className="flex items-center gap-3">
                {renderCompletionBadge(selectedVehicle)}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => openEditModal(selectedVehicle)}
                    className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    {common.edit}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedVehicle(null)}
                  className="text-slate-400 hover:text-white px-3 py-2"
                >
                  关闭
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <section>
                <h4 className="text-slate-200 font-semibold mb-3">车辆照片</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderPhotoPanel('正面照片', selectedVehicle.front_photo_url, '车辆正面照片')}
                  {renderPhotoPanel('车内座位照片', selectedVehicle.seat_photo_url, '车内座位照片')}
                </div>
              </section>

              <section>
                <h4 className="text-slate-200 font-semibold mb-3">基本信息</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">车牌号</p><p className="text-white">{selectedVehicle.plate_number || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">车架号</p><p className="text-white">{selectedVehicle.chassis_number || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">最多乘坐人数</p><p className="text-white">{selectedVehicle.max_passengers ? `${selectedVehicle.max_passengers}名` : '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">车型</p><p className="text-white">{getVehicleTypeLabel(selectedVehicle) || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">品牌 / 型号</p><p className="text-white">{[selectedVehicle.vehicle_make, selectedVehicle.vehicle_model].filter(Boolean).join(' / ') || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">年式 / 颜色</p><p className="text-white">{[selectedVehicle.vehicle_year, selectedVehicle.vehicle_color].filter(Boolean).join(' / ') || '-'}</p></div>
                </div>
              </section>

              <section>
                <h4 className="text-slate-200 font-semibold mb-3">证件 / 期限</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">保险证号</p><p className="text-white">{selectedVehicle.insurance_number || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">保险到期日</p><p className="text-white">{selectedVehicle.insurance_expiry || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">车检到期日</p><p className="text-white">{selectedVehicle.inspection_expiry || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">三个月点检</p><p className="text-white">{getNextOpenQuarterlyInspection(selectedVehicle)?.expected_date || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">到期提醒</p><p className="text-white">{getVehicleExpirySummary(selectedVehicle)}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">资料状态</p>{renderCompletionBadge(selectedVehicle)}</div>
                </div>
              </section>

              <section>
                <h4 className="text-slate-200 font-semibold mb-3">运营信息</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">负责司机</p><p className="text-white">{selectedVehicle.assigned_driver_name || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">ETC卡号</p><p className="text-white">{selectedVehicle.etc_card_number || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">状态</p><p className="text-white">{statusOptions.find(s => s.value === selectedVehicle.status)?.label || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3"><p className="text-slate-500">设备</p><p className="text-white">{[
                    selectedVehicle.has_dashcam ? '行车记录仪' : '',
                    selectedVehicle.has_winter_tires ? '冬胎' : '',
                    selectedVehicle.has_gps ? 'GPS' : '',
                  ].filter(Boolean).join(' / ') || '-'}</p></div>
                  <div className="bg-slate-900/40 rounded-lg p-3 md:col-span-2"><p className="text-slate-500">备注</p><p className="text-white whitespace-pre-wrap">{selectedVehicle.notes || '-'}</p></div>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-3xl border border-slate-700 max-h-[90vh] overflow-y-auto">
            <h3 className="text-white text-xl font-bold mb-6">
              {editingVehicle ? t.editTitle : t.addTitle}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* 车辆照片 */}
              <div>
                <h4 className="text-slate-300 font-medium mb-3 border-b border-slate-700 pb-2">车辆照片</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <label className="block text-slate-400 text-sm">正面照片</label>
                    <div className="h-52 rounded-lg border border-slate-700 bg-slate-900/40 overflow-hidden flex items-center justify-center">
                      {form.front_photo_url ? (
                        <img
                          src={form.front_photo_url}
                          alt="车辆正面照片预览"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-slate-500 text-sm">未上传</span>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload('front_photo_url', e.target.files?.[0])}
                      className="block w-full text-sm text-slate-300 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-white hover:file:bg-blue-500"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-slate-400 text-sm">车内座位照片</label>
                    <div className="h-52 rounded-lg border border-slate-700 bg-slate-900/40 overflow-hidden flex items-center justify-center">
                      {form.seat_photo_url ? (
                        <img
                          src={form.seat_photo_url}
                          alt="车内座位照片预览"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-slate-500 text-sm">未上传</span>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload('seat_photo_url', e.target.files?.[0])}
                      className="block w-full text-sm text-slate-300 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-white hover:file:bg-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* 基本信息 */}
              <div>
                <h4 className="text-slate-300 font-medium mb-3 border-b border-slate-700 pb-2">基本信息</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">当前车牌号 *</label>
                    <input
                      type="text"
                      value={form.plate_number}
                      onChange={(e) => setForm({ ...form, plate_number: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">原车牌号</label>
                    <input
                      type="text"
                      value={form.original_plate}
                      onChange={(e) => setForm({ ...form, original_plate: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">原所有人</label>
                    <input
                      type="text"
                      value={form.original_owner}
                      onChange={(e) => setForm({ ...form, original_owner: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">车架号</label>
                    <input
                      type="text"
                      value={form.chassis_number}
                      onChange={(e) => setForm({ ...form, chassis_number: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">最多乘坐人数</label>
                    <input
                      type="number"
                      min="1"
                      value={form.max_passengers}
                      onChange={(e) => setForm({ ...form, max_passengers: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">品牌</label>
                    <input
                      type="text"
                      value={form.vehicle_make}
                      onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                      placeholder="Toyota"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">型号</label>
                    <input
                      type="text"
                      value={form.vehicle_model}
                      onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                      placeholder="Alphard / Hiace"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">年式 / 登録年</label>
                    <input
                      type="number"
                      min="1990"
                      max="2100"
                      value={form.vehicle_year}
                      onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">车身颜色</label>
                    <input
                      type="text"
                      value={form.vehicle_color}
                      onChange={(e) => setForm({ ...form, vehicle_color: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">车型 *</label>
                    <select
                      value={form.vehicle_type}
                      onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    >
                      {vehicleTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  {form.vehicle_type === 'other' && (
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">其他车型说明</label>
                      <input
                        type="text"
                        value={form.vehicle_type_other}
                        onChange={(e) => setForm({ ...form, vehicle_type_other: e.target.value })}
                        className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">状态</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    >
                      {statusOptions.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 配置状态 */}
              <div>
                <h4 className="text-slate-300 font-medium mb-3 border-b border-slate-700 pb-2">运营信息</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">ETC卡号</label>
                    <input
                      type="text"
                      value={form.etc_card_number}
                      onChange={(e) => setForm({ ...form, etc_card_number: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div className="flex items-center gap-6 pt-6">
                    <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.has_dashcam}
                        onChange={(e) => setForm({ ...form, has_dashcam: e.target.checked })}
                        className="w-5 h-5 rounded bg-slate-700 border-slate-600"
                      />
                      行车记录仪
                    </label>
                    <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.has_winter_tires}
                        onChange={(e) => setForm({ ...form, has_winter_tires: e.target.checked })}
                        className="w-5 h-5 rounded bg-slate-700 border-slate-600"
                      />
                      冬胎
                    </label>
                    <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.has_gps}
                        onChange={(e) => setForm({ ...form, has_gps: e.target.checked })}
                        className="w-5 h-5 rounded bg-slate-700 border-slate-600"
                      />
                      GPS
                    </label>
                  </div>
                </div>
              </div>

              {/* 保险与车检 */}
              <div>
                <h4 className="text-slate-300 font-medium mb-3 border-b border-slate-700 pb-2">证件 / 期限</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">保险种类</label>
                    <select
                      value={form.insurance_type}
                      onChange={(e) => setForm({ ...form, insurance_type: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    >
                      {insuranceTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">保险证号</label>
                    <input
                      type="text"
                      value={form.insurance_number}
                      onChange={(e) => setForm({ ...form, insurance_number: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">保险到期日</label>
                    <input
                      type="date"
                      value={form.insurance_expiry}
                      onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">车检到期日</label>
                    <input
                      type="date"
                      value={form.inspection_expiry}
                      onChange={(e) => setForm({ ...form, inspection_expiry: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* 三个月点检 */}
              <div>
                <h4 className="text-slate-300 font-medium mb-3 border-b border-slate-700 pb-2">三个月点检记录</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">第一次预计3个月点检日期</label>
                    <input
                      type="date"
                      value={form.quarterly_inspection_start_date}
                      onChange={(e) => setForm({ ...form, quarterly_inspection_start_date: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    />
                  </div>
                  <div className="text-slate-400 text-sm flex items-end">
                    <span>系统会每3个月自动生成日程，页面保留历史点检记录，并显示未来4次。</span>
                  </div>
                </div>

                {quarterlyInspectionSchedule.length > 0 && (
                  <div className="space-y-4">
                    <div className="bg-slate-900/40 rounded-lg border border-slate-700 overflow-hidden">
                      <div className="px-4 py-3 bg-slate-700/40 text-slate-300 text-sm font-medium">
                        未来4次
                      </div>
                      <div className="divide-y divide-slate-700">
                        {quarterlyFutureRows.map((row) => (
                          <div key={row.expected_date} className="grid grid-cols-1 md:grid-cols-2 gap-3 px-4 py-3">
                            <div>
                              <p className="text-slate-500 text-xs">预计点检日期</p>
                              <p className="text-white">{row.expected_date}</p>
                            </div>
                            <div>
                              <label className="block text-slate-500 text-xs mb-1">实际点检日期</label>
                              <input
                                type="date"
                                value={row.actual_date || ''}
                                onChange={(e) => updateQuarterlyActualDate(row.expected_date, e.target.value)}
                                className="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {quarterlyHistoryRows.length > 0 && (
                      <div className="bg-slate-900/40 rounded-lg border border-slate-700 overflow-hidden">
                        <div className="px-4 py-3 bg-slate-700/40 text-slate-300 text-sm font-medium">
                          历史点检记录
                        </div>
                        <div className="divide-y divide-slate-700">
                          {quarterlyHistoryRows.map((row) => (
                            <div key={row.expected_date} className="grid grid-cols-1 md:grid-cols-2 gap-3 px-4 py-3">
                              <div>
                                <p className="text-slate-500 text-xs">预计点检日期</p>
                                <p className={row.actual_date ? 'text-slate-300' : 'text-red-400 font-medium'}>
                                  {row.expected_date}
                                  {!row.actual_date && <span className="ml-2 text-xs">未填写</span>}
                                </p>
                              </div>
                              <div>
                                <label className="block text-slate-500 text-xs mb-1">实际点检日期</label>
                                <input
                                  type="date"
                                  value={row.actual_date || ''}
                                  onChange={(e) => updateQuarterlyActualDate(row.expected_date, e.target.value)}
                                  className="w-full bg-slate-700 border border-slate-600 text-white px-3 py-2 rounded-lg"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 关联与文档 */}
              <div>
                <h4 className="text-slate-300 font-medium mb-3 border-b border-slate-700 pb-2">关联与文档</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">负责司机</label>
                    <select
                      value={form.assigned_driver_id}
                      onChange={(e) => setForm({ ...form, assigned_driver_id: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    >
                      <option value="">-- 未分配 --</option>
                      {drivers.filter(d => d.status === 'active').map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Documents Link (Box URL)</label>
                    <input
                      type="url"
                      value={form.documents_link}
                      onChange={(e) => setForm({ ...form, documents_link: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                      placeholder="https://box.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-slate-400 text-sm mb-1">备注</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-6 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  {common.cancel}
                </button>
                <button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/30 transition-all"
                >
                  {common.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetVehicles;
